import { useEffect, useState, type ComponentType } from "react";
import { runWhenIdle } from "@/lib/idle";

// The Toaster is only needed after a user action (copy, save, login), so it is
// loaded once the browser is idle or on the first interaction, whichever comes first,
// instead of ending up in the entry bundle.
export function DeferredToaster() {
  const [Toaster, setToaster] = useState<ComponentType | null>(null);

  useEffect(() => {
    let started = false;
    let cancelled = false;
    const interactionEvents = ["pointerdown", "keydown", "touchstart"] as const;

    const removeInteractionListeners = () => {
      for (const name of interactionEvents) window.removeEventListener(name, start);
    };

    function start() {
      if (started) return;
      started = true;
      cancelIdle();
      removeInteractionListeners();
      import("@/components/ui/sonner")
        .then((module) => {
          if (!cancelled) setToaster(() => module.Toaster);
        })
        .catch(() => {});
    }

    const cancelIdle = runWhenIdle(start);
    for (const name of interactionEvents) window.addEventListener(name, start, { passive: true });

    return () => {
      cancelled = true;
      cancelIdle();
      removeInteractionListeners();
    };
  }, []);

  // Sonner drops toasts published before <Toaster> subscribes (e.g. a failed copy right
  // after a cold load). They stay in its history, so show them once the Toaster is mounted.
  // Child effects run first, so the Toaster is already subscribed here.
  useEffect(() => {
    if (!Toaster) return;
    import("sonner")
      .then(({ toast }) => {
        for (const item of toast.getToasts()) {
          if ("title" in item) toast.message(item.title, item);
        }
      })
      .catch(() => {});
  }, [Toaster]);

  return Toaster ? <Toaster /> : null;
}
